const PREMIUM_HOSTNAME = 'premium.hcap-analytics.com';
const isPremiumHost = () => window.location.hostname.toLowerCase() === PREMIUM_HOSTNAME;

angular.module('hcap-analytics')
.config([
  '$stateProvider',
  '$urlRouterProvider',
  '$locationProvider',
  '$qProvider',
  '$transitionsProvider',
  'portalsConfig',
function(
  $stateProvider,
  $urlRouterProvider,
  $locationProvider,
  $qProvider,
  $transitionsProvider,
  portalsConfig,
) {
  $qProvider.errorOnUnhandledRejections(false);
  $locationProvider.html5Mode({
    enabled: true,
    rewriteLinks: false,
  });
  $locationProvider.hashPrefix('');

  $urlRouterProvider.otherwise(injector => {
    const $location = injector.get('$location');
    $location.path(isPremiumHost() ? '/Premium' : '/v2');
  });

  $transitionsProvider.onStart({}, trans => {
    if (trans) {
      const userService = trans.injector().get('UserService');
      const signedIn = userService.isSignedIn();
      const portal = trans.params().portal;

      if (trans.to().name === 'landing' && isPremiumHost()) {
        return trans.router.stateService.target('premium', trans.params(), {
          inherit: false,
          location: 'replace',
        });
      }
      const canonicalPortal = portal && Object.keys(portalsConfig).find(key => key.toLowerCase() === String(portal).toLowerCase());
      const bundleToken = trans.params().bundleToken ||
        (/-bundle$/.test(trans.to().name) && trans.params().token);
      const bundleCohortId = trans.params().cohortId;

      const bundleAccess = trans.to().name.indexOf('participate') === 0 &&
        trans.params().bundleToken &&
        trans.params().cohortId;

      if (portal && canonicalPortal && canonicalPortal !== portal) {
        return trans.router.stateService.target(trans.to().name, {
          ...trans.params(),
          portal: canonicalPortal,
        }, {
          inherit: false,
          location: 'replace',
        });
      }

      if (portal && !canonicalPortal) {
        return trans.router.stateService.target('landing');
      }

      if (bundleToken && bundleCohortId) {
        const surveyBundleService = trans.injector().get('SurveyBundleService');
        return surveyBundleService.authenticateBundle(bundleCohortId, bundleToken).then(user => {
          userService.updateUser(user);
        }).catch(() => {
          const landingState = canonicalPortal && portalsConfig[canonicalPortal] ?
            portalsConfig[canonicalPortal].landingState :
            'landing';

          return trans.router.stateService.target(landingState);
        });
      }

      if (trans.to().authenticate && !signedIn && !bundleAccess) {
        const landingState = portal && portalsConfig[portal] ?
          portalsConfig[portal].landingState :
          'landing';

        return trans.router.stateService.target(landingState, {
          redirect: {
            state: trans.to().name,
            params: trans.params(),
          },
        });
      } else if (trans.to().name === 'signin' && signedIn) {
        userService.signOut();
      }
    } else {
      return true;
    }
  });

  $transitionsProvider.onBefore({to: 'participate.questions'}, trans => {
    if (!trans.params().asRater) {
      const userService = trans.injector().get('UserService');
      return userService.hasSetConsentStatus().then(hasSetConsent => {
        if (!hasSetConsent) {
          if (trans.params().portal === 'CSM') {
            return trans.router.stateService.target('csm-consent', {
              portal: trans.params().portal,
              redirectState: trans.to().name,
              redirectParams: trans.params(),
            });
          }

          if (trans.params().bundleToken) {
            return true;
          }

          return trans.router.stateService.target('profile', {
            portal: trans.params().portal,
            promptFill: true,
          });
        }
      });
    }
  });

  $stateProvider
    .state('landing', {
      url: '/',
      templateUrl: 'templates/landing.html',
      controller: 'LandingCtrl',
      params: {
        redirect: null,
      },
      redirectTo: trans => {
        const user = localStorage.getItem('user');
        const locationService = trans.injector().get('$location');
        const src = locationService.search().src;

        if (src && !user) {
          localStorage.setItem('src', src);
          return { state: 'landing-consent' };
        } else {
          return true;
        }
      },
    })
    .state('landing-consent', {
      url: '/consent',
      templateUrl: 'templates/landingConsent.html',
      controller: 'LandingConsentCtrl',
      params: {
        redirect: null,
      },
    })
    .state('csm', {
      url: '/CSM',
      params: {
        portal: 'CSM',
      },
      templateUrl: 'templates/csmLanding.html',
      controller: 'CsmLandingCtrl',
      redirectTo: trans => {
        const userService = trans.injector().get('UserService');
        if (userService.isSignedIn()) {
          return {
            state: 'dashboard',
            params: {
              portal: 'CSM',
            },
          };
        }
        return true;
      },
    })
    .state('csm-lower', {
      url: '/csm',
      redirectTo: () => ({
        state: 'csm',
        params: {
          portal: 'CSM',
        },
      }),
    })
    .state('csm-bundle', {
      url: '/CSM/bundle/:cohortId/:token',
      params: {
        portal: 'CSM',
      },
      templateUrl: 'templates/csmBundle.html',
      controller: 'CsmBundleCtrl',
    })
    .state('csm-bundle-lower', {
      url: '/csm/bundle/:cohortId/:token',
      redirectTo: trans => ({
        state: 'csm-bundle',
        params: {
          portal: 'CSM',
          cohortId: trans.params().cohortId,
          token: trans.params().token,
        },
      }),
    })
    .state('csm-admin', {
      url: '/CSM/admin/:cohortId/:token',
      params: {
        portal: 'CSM',
      },
      templateUrl: 'templates/csmAdmin.html',
      controller: 'CsmAdminCtrl',
    })
    .state('csm-admin-lower', {
      url: '/csm/admin/:cohortId/:token',
      redirectTo: trans => ({
        state: 'csm-admin',
        params: {
          portal: 'CSM',
          cohortId: trans.params().cohortId,
          token: trans.params().token,
        },
      }),
    })
    .state('csm-consent', {
      url: '/CSM/consent',
      params: {
        portal: 'CSM',
        redirectState: null,
        redirectParams: null,
      },
      templateUrl: 'templates/csmConsent.html',
      controller: 'CsmConsentCtrl',
      authenticate: true,
    })
    .state('csm-consent-lower', {
      url: '/csm/consent',
      redirectTo: trans => ({
        state: 'csm-consent',
        params: {
          portal: 'CSM',
          redirectState: trans.params().redirectState,
          redirectParams: trans.params().redirectParams,
        },
      }),
    })
    .state('premium-bundle', {
      url: '/Premium/bundle/:cohortId/:token',
      params: {
        portal: 'Premium',
      },
      templateUrl: 'templates/csmBundle.html',
      controller: 'CsmBundleCtrl',
    })
    .state('premium-bundle-lower', {
      url: '/premium/bundle/:cohortId/:token',
      redirectTo: trans => ({
        state: 'premium-bundle',
        params: {
          portal: 'Premium',
          cohortId: trans.params().cohortId,
          token: trans.params().token,
        },
      }),
    })
    .state('premium-admin', {
      url: '/Premium/admin/:cohortId/:token',
      params: {
        portal: 'Premium',
      },
      templateUrl: 'templates/csmAdmin.html',
      controller: 'CsmAdminCtrl',
    })
    .state('premium-admin-lower', {
      url: '/premium/admin/:cohortId/:token',
      redirectTo: trans => ({
        state: 'premium-admin',
        params: {
          portal: 'Premium',
          cohortId: trans.params().cohortId,
          token: trans.params().token,
        },
      }),
    })
    .state('premium', {
      url: '/Premium',
      params: {
        portal: 'Premium',
      },
      templateUrl: 'templates/premiumLanding.html',
      controller: 'PremiumLandingCtrl',
      redirectTo: trans => {
        const userService = trans.injector().get('UserService');
        if (userService.isSignedIn()) {
          return {
            state: 'dashboard',
            params: {
              portal: 'Premium',
            },
          };
        }
        return true;
      },
    })
    .state('premium-lower', {
      url: '/premium',
      redirectTo: () => ({
        state: 'premium',
        params: {
          portal: 'Premium',
        },
      }),
    })
    .state('home', {
      url: '/:portal',
      params: {
        portal: 'v2',
      },
      redirectTo: trans => {
        const injector = trans.injector();
        const userService = injector.get('UserService');

        if (userService.isSignedIn()) {
          const src = localStorage.getItem('src');

          if (src) {
            const assessmentService = injector.get('AssessmentService');
            //const sectionsConfig = injector.get('sectionsConfig');
            const util = injector.get('Util');

            return assessmentService.getUserSelfAssessments().then(assessments => {
              // SPAC1
              if (src === 'SPABC') {
                return util.viewAssessmentState('ccW0JZ.UItTlSGTkzmQO7F', 'gxTln32xIHmJGEmYIZEZpk', null, {
                  finishRoute: 'spabc-finish',
                });
              // SPAC2
              } else if (src === 'SPABC2') {
                return util.viewAssessmentState('i66Dc9hsHfP414ypAgNL0V', 'LmgRc_KUF7PNAZAKWPR4lV', null, {
                  finishRoute: 'spabc-finish',
                });
              }

              //const atworkTemplateGuid = '5vY5kxU8GDi.QlHjBIzC8k';
              //const section = sectionsConfig.find(section => section.id === 'hexaco');
              //const assessmentData = section.assessments.find(a => a.templateGuid === atworkTemplateGuid);
              //const part = assessments.find(a => a.template_guid === atworkTemplateGuid && a.participant)?.participant;
/*
              if (part && +new Date(part.startTime) < 1680648435300) {
                return { state: 'dashboard' };
              }
*/
              //return util.viewAssessmentState(assessmentData.id, assessmentData.templateGuid, part);
            }).then(state => {
              return trans.router.stateService.target(state.state, state.params, state.options);
            });
          } else {
            return {
              state: 'dashboard',
              params: {
                portal: trans.params().portal,
              },
            };
          }
        } else {
          return { state: 'landing' };
        }
      },
    })
    .state('dashboard', {
      url: '/:portal/dashboard',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/dashboard.html',
      controller: 'DashboardCtrl',
      authenticate: true,
    })
    .state('view-dev-plan', {
      url: '/:portal/view-dev-plan?guid?email?code',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/devPlans.html',
      redirectTo: trans => {
        const params = trans.params();

        if (!params.guid) {
          return { state: 'landing' };
        }

        return userPassthrough({
          params,
          userService: trans.injector().get('UserService'),
          routeData: {
            state: 'dev-plan',
            params: {
              portal: params.portal,
              guid: params.guid,
            },
          },
        });
      },
    })
    .state('dev-plan', {
      url: '/:portal/dev-plan?guid',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/devPlans.html',
      controller: 'DevPlanCtrl',
      authenticate: true,
    })
    .state('participate', {
      url: '/:portal/participate/:templateId/:participantId?bundleToken&cohortId',
      templateUrl: 'templates/participate_v2.html',
      controller: 'ParticipateCtrlv2',
      authenticate: true,
      params: {
        portal: 'v2',
      },
    })
    .state('participate.questions', {
      url: '/questions?pageIndex&showRaterPrompt',
      templateUrl: 'templates/questions.html',
      controller: 'QuestionsCtrl',
      authenticate: true,
      params: {
        asRater: null,
        finishRoute: null,
        finishRouteParams: null,
        onFinish: null,
      },
    })
    .state('participate.result', {
      url: '/result',
      templateUrl: 'templates/participateResult.html',
      authenticate: true,
    })
    .state('participate.plan', {
      url: '/plan',
      templateUrl: 'templates/devPlan.html',
      controller: 'DevPlanCtrl',
      authenticate: true,
    })
    .state('participate.rater-finish', {
      url: '/feedback-finished',
      templateUrl: 'templates/rater-finish.html',
      controller: 'RaterFinishCtrl',
      authenticate: true,
    })
    .state('sectionPage', {
      url: '/:portal/section/:id',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/section.html',
      controller: 'SectionCtrl',
      authenticate: true,
    })
    .state('profile', {
      url: '/:portal/profile?require',
      templateUrl: 'templates/profile_v2.html',
      controller: 'ProfileCtrl',
      authenticate: true,
      params: {
        portal: 'v2',
        promptFill: null,
      },
    })
    .state('my-teams', {
      redirectTo: trans => {
        const userService = trans.injector().get('UserService');
        const cohortsService = trans.injector().get('CohortsService');

        if (userService.canManagePortal(trans.params().portal)) {
          return {
            state: 'cohorts',
            params: {
              portal: trans.params().portal,
            },
          };
        } else {
          return cohortsService.summary().then(summaries => {
            if (summaries.length === 1) {
              const cohort = summaries[0];

              return {
                state: 'team',
                params: {
                  portal: trans.params().portal,
                  id: cohort._id,
                },
              };
            } else {
              return {
                state: 'teams',
                params: {
                  portal: trans.params().portal,
                },
              };
            }
          });
        }
      },
    })
    .state('teams', {
      url: '/:portal/teams',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/teams.html',
      controller: 'TeamsCtrl',
      authenticate: true,
    })
    .state('team', {
      url: '/:portal/team/:id?phase',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/team.html',
      controller: 'TeamCtrl',
      authenticate: true,
    })
    .state('team.questions', {
      url: '/questions?pageIndex',
      templateUrl: 'templates/questions.html',
      controller: 'QuestionsCtrl',
      authenticate: true,
      params: {
        templateId: null,
        participantId: null,
        loadRoute: null,
        finishRoute: null,
        finishRouteParams: null,
        teamMembers: null,
      },
    })
    .state('cohorts', {
      url: '/:portal/cohorts',
      params: {
        portal: 'v2',
      },
      templateProvider: [
        '$templateRequest',
        '$stateParams',
        'portalsConfig',
        function($templateRequest, $stateParams, portalsConfig) {
          return $templateRequest(portalsConfig[$stateParams.portal].cohorts.templateUrl);
        },
      ],
      controllerProvider: [
        '$stateParams',
        'portalsConfig',
        function($stateParams, portalsConfig) {
          return portalsConfig[$stateParams.portal].cohorts.controller;
        },
      ],
      authenticate: true,
      redirectTo: trans => {
        const userService = trans.injector().get('UserService');
        if (userService.canManagePortal(trans.params().portal)) {
          return true;
        } else {
          return {
            state: 'dashboard',
            params: {
              portal: trans.params().portal,
            },
          }
        }
      },
    })
    .state('activate', {
      url: '/:portal/activate',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/activate.html',
      controller: 'ActivateCtrl',
    })
    .state('rate', {
      url: '/:portal/rate?email&code&templateId&participantId',
      params: {
        portal: 'v2',
      },
      redirectTo: trans => {
        const params = trans.params();

        if (!params.templateId || !params.participantId) {
          return { state: 'landing' };
        }

        return userPassthrough({
          params,
          userService: trans.injector().get('UserService'),
          routeData:{
            state: 'participate.questions',
            params: {
              portal: params.portal,
              asRater: true,
              templateId: params.templateId,
              participantId: params.participantId,
              finishRoute: 'participate.rater-finish',
              onFinish: trans.injector().get('InvitesService').update,
            },
          }
        });
      },
    })
    .state('rate-bundle', {
      url: '/:portal/rate-bundle?email&code&participantId',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/csmRaterBundle.html',
      controller: 'CsmRaterBundleCtrl',
      redirectTo: trans => {
        const params = trans.params();
        const userService = trans.injector().get('UserService');

        if (!params.participantId) {
          return { state: 'landing' };
        }

        if (userService.isSignedIn()) {
          return true;
        }

        const result = userPassthrough({
          params,
          userService,
          routeData: {
            state: 'rate-bundle',
            params: {
              portal: params.portal,
              participantId: params.participantId,
            },
          },
        });

        if (result && typeof result.then === 'function') {
          return result.then(resolved => {
            if (resolved && resolved.state === 'rate-bundle') {
              return true;
            }
            return resolved;
          });
        }

        return result;
      },
    })
    .state('cohort', {
      url: '/:portal/cohort/:id',
      params: {
        portal: 'v2',
      },
      templateProvider: [
        '$templateRequest',
        '$stateParams',
        'portalsConfig',
        function($templateRequest, $stateParams, portalsConfig) {
          return $templateRequest(portalsConfig[$stateParams.portal].cohort.templateUrl);
        },
      ],
      controllerProvider: [
        '$stateParams',
        'portalsConfig',
        function($stateParams, portalsConfig) {
          return portalsConfig[$stateParams.portal].cohort.controller;
        },
      ],
      authenticate: true,
      redirectTo: trans => {
        const userService = trans.injector().get('UserService');

        if (userService.canManagePortal(trans.params().portal)) {
          return true;
        } else {
          return {
            state: 'dashboard',
            params: {
              portal: trans.params().portal,
            },
          }
        }
      },
    })
    .state('sectionOrg', {
      url: '/:portal/section-org',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/sectionOrg.html',
      authenticate: true,
    })
    .state('teamNext', {
      url: '/:portal/teams-next',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/teamNext.html',
      controller: 'TeamNextCtrl',
      authenticate: true,
      redirectTo: trans => {
        const userService = trans.injector().get('UserService');

        if (userService.hasTeams() || userService.canManagePortal(trans.params().portal)) {
          return true;
        } else {
          return {
            state: 'sectionPage',
            params: {
              portal: trans.params().portal,
              id: 'team_self',
            },
          };
        }
      },
    })
    .state('invites', {
      url: '/:portal/invites',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/invites.html',
      controller: 'InvitesCtrl',
      authenticate: true,
    })
    .state('spabc-finish', {
      url: '/:portal/spabc-finish',
      params: {
        portal: 'v2',
      },
      templateUrl: 'templates/spabc-finish.html',
    });

  function userPassthrough({ routeData, userService, params }) {
    if (userService.isSignedIn()) {
      // logged in, redirect
      return routeData;
    } else {
      if (params.code === 'null') {
        // full account exists but logged out, redirect on log in
        return {
          state: 'landing',
          params: {
            redirect: routeData,
          },
        };
      } else if (params.email) {
        return new Promise(resolve => {
          // attempt to login designated user and redirect
          userService.signIn({
            email: params.email,
            code: params.code,
          }, user => {
            if (user) {
              resolve(routeData);
            } else {
              resolve({ state: 'landing' });
            }
          });
        });
      } else {
        return { state: 'landing' };
      }
    }
  }
}]);
